/**
 * Service Invoice Tab — Slice 3B (qty-based FIFO allocation).
 * User types an "Invoiced Quantity" + optional date range; server FIFO-allocates
 * across pending work-details (oldest first). Live preview shows which work-details
 * will be consumed (full or partial) before saving.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Save, Receipt, Trash2, FileText, Calendar, AlertCircle } from 'lucide-react';
import { siteProjectAPI } from '../../services/api';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmt3 = (n) => (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const CreateDialog = ({ project, onClose, onSaved }) => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [qty, setQty] = useState(0);
  const [rate, setRate] = useState(Number(project.rate_per_unit) || 0);
  const [gstRate, setGstRate] = useState(18);
  const [isIntraState, setIsIntraState] = useState(true);
  const [remarks, setRemarks] = useState('');
  const [summary, setSummary] = useState({ pending_qty: 0, rows: [] });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch pending work-summary (refresh when date range changes)
  useEffect(() => {
    setSummaryLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await siteProjectAPI.pendingWorkSummary(project.id, { date_from: dateFrom, date_to: dateTo });
        setSummary(r?.service || { pending_qty: 0, rows: [] });
      } catch { /* ignore */ } finally { setSummaryLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [project.id, dateFrom, dateTo]);

  // Compute FIFO allocation plan client-side for preview
  const plan = (() => {
    const out = [];
    let left = Number(qty) || 0;
    for (const r of summary.rows) {
      if (left <= 0) break;
      const take = Math.min(left, r.remaining);
      out.push({ id: r.id, date: r.date, consumed: Math.round(take * 1000) / 1000, total: r.total, remaining_after: Math.round((r.remaining - take) * 1000) / 1000, unit: r.unit });
      left -= take;
    }
    return { items: out, short: left > 0.001 ? Math.round(left * 1000) / 1000 : 0 };
  })();

  const subtotal = Math.round((Number(qty) || 0) * (Number(rate) || 0) * 100) / 100;
  const tax = Math.round(subtotal * (Number(gstRate) || 0) / 100 * 100) / 100;
  const cgst = isIntraState ? Math.round(tax / 2 * 100) / 100 : 0;
  const sgst = isIntraState ? tax - cgst : 0;
  const igst = isIntraState ? 0 : tax;
  const total = Math.round((subtotal + tax) * 100) / 100;

  const applyMax = () => setQty(summary.pending_qty);

  const handleSave = async () => {
    setError('');
    if (!qty || qty <= 0) { setError('Invoiced quantity must be > 0'); return; }
    if (plan.short > 0) { setError(`Insufficient pending qty. Short by ${fmt3(plan.short)} ${summary.rows[0]?.unit || ''}.`); return; }
    if (!rate || rate <= 0) { setError('Rate must be > 0'); return; }
    setSaving(true);
    try {
      await siteProjectAPI.createServiceInvoice(project.id, {
        project_id: project.id, date,
        date_from: dateFrom || null, date_to: dateTo || null,
        total_quantity: qty, rate_per_unit: rate,
        quantity_unit: summary.rows[0]?.unit || '',
        gst_rate: gstRate, is_intra_state: isIntraState,
        subtotal, cgst, sgst, igst, total, remarks,
      });
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Create Service Invoice</h3>
            <p className="text-[11px] text-gray-400">Qty-based — allocates FIFO across pending work-details</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-4">
          {/* Date range filter */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Invoice Date *</label>
              <input data-testid="si-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date From</label>
              <input data-testid="si-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Date To</label>
              <input data-testid="si-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Pending Qty</label>
              <div className="flex items-center gap-1">
                <p className="flex-1 px-2 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-lg" data-testid="si-pending-qty">{summaryLoading ? 'Loading…' : `${fmt3(summary.pending_qty)} ${summary.rows[0]?.unit || ''}`}</p>
                <button type="button" onClick={applyMax} className="px-2 py-2 text-[10px] text-blue-600 font-semibold hover:bg-blue-50 rounded-lg" title="Use all pending qty">Max</button>
              </div>
            </div>
          </div>

          {/* Qty + rate */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Invoiced Qty *</label>
              <input data-testid="si-qty" type="number" value={qty || ''} onChange={e => setQty(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Rate / Unit * <span className="text-[9px] text-gray-400">₹</span></label>
              <input data-testid="si-rate" type="number" value={rate || ''} onChange={e => setRate(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">GST %</label>
              <input data-testid="si-gst" type="number" value={gstRate} onChange={e => setGstRate(parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" data-testid="si-intra-state" checked={isIntraState} onChange={e => setIsIntraState(e.target.checked)} />
            <span>Intra-state (CGST + SGST) — uncheck for IGST</span>
          </label>

          {/* Live FIFO allocation preview */}
          {qty > 0 && (
            <div className="border border-blue-100 rounded-lg overflow-hidden" data-testid="si-allocation-preview">
              <div className="bg-blue-50 px-3 py-1.5 text-[10px] font-bold text-blue-700 uppercase tracking-wider">
                Allocation preview {plan.short > 0 && <span className="text-red-600 normal-case font-normal">— short by {fmt3(plan.short)}</span>}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-blue-50/50 text-[10px] text-blue-500 uppercase">
                    <th className="text-left py-1.5 px-3">Work Date</th>
                    <th className="text-right py-1.5 px-3">Total</th>
                    <th className="text-right py-1.5 px-3">Consumed</th>
                    <th className="text-right py-1.5 px-3">Remaining After</th>
                    <th className="text-left py-1.5 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.items.map(p => (
                    <tr key={p.id} className="border-t border-blue-50">
                      <td className="py-1.5 px-3 text-gray-600">{p.date}</td>
                      <td className="py-1.5 px-3 text-right text-gray-500">{fmt3(p.total)} {p.unit}</td>
                      <td className="py-1.5 px-3 text-right font-semibold text-blue-700">{fmt3(p.consumed)}</td>
                      <td className="py-1.5 px-3 text-right text-gray-500">{fmt3(p.remaining_after)}</td>
                      <td className="py-1.5 px-3">
                        {p.remaining_after <= 0.001 ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">Fully invoiced</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Partial</span>}
                      </td>
                    </tr>
                  ))}
                  {plan.items.length === 0 && (<tr><td colSpan={5} className="py-3 px-3 text-center text-gray-400">No allocations (increase qty)</td></tr>)}
                </tbody>
              </table>
            </div>
          )}

          {/* Summary */}
          <div className="p-3 bg-blue-50 rounded-lg space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-blue-600">Subtotal</span><span className="font-semibold text-blue-800">₹{fmt(subtotal)}</span></div>
            {isIntraState ? (
              <>
                <div className="flex justify-between text-blue-600"><span>CGST ({gstRate / 2}%)</span><span>₹{fmt(cgst)}</span></div>
                <div className="flex justify-between text-blue-600"><span>SGST ({gstRate / 2}%)</span><span>₹{fmt(sgst)}</span></div>
              </>
            ) : (
              <div className="flex justify-between text-blue-600"><span>IGST ({gstRate}%)</span><span>₹{fmt(igst)}</span></div>
            )}
            <div className="flex justify-between pt-1 border-t border-blue-200"><span className="text-blue-700 font-bold">Total</span><span className="text-blue-800 font-bold" data-testid="si-total">₹{fmt(total)}</span></div>
          </div>

          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Remarks</label>
            <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={2} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
          </div>

          {error && <div className="p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex items-start gap-2"><AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /><span>{error}</span></div>}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button data-testid="si-save-btn" onClick={handleSave} disabled={saving || qty <= 0 || rate <= 0 || plan.short > 0} className="px-4 py-2 text-xs text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
            <Save className="w-3.5 h-3.5 inline mr-1" />{saving ? 'Saving…' : 'Create Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ServiceInvoiceTab = ({ project }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const inv = await siteProjectAPI.listServiceInvoices(project.id);
      setInvoices(inv || []);
    } finally { setLoading(false); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (invId) => {
    if (!window.confirm('Delete this service invoice? Linked work-detail allocations will be released.')) return;
    await siteProjectAPI.deleteServiceInvoice(project.id, invId);
    load();
  };

  const totalInvoiced = invoices.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const totalSubtotal = invoices.reduce((s, i) => s + (Number(i.subtotal) || 0), 0);
  const totalQty = invoices.reduce((s, i) => s + (Number(i.total_quantity) || 0), 0);

  return (
    <div className="space-y-4" data-testid="service-invoice-tab">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Receipt className="w-4 h-4" /> Service Invoices</h4>
          <p className="text-[11px] text-gray-400">Proforma with GST · Qty-based FIFO allocation to work-details</p>
        </div>
        <button data-testid="si-add-btn" onClick={() => setDialogOpen(true)} className="px-3 py-1.5 text-xs text-white rounded-lg flex items-center gap-1" style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-3.5 h-3.5" /> Create Invoice
        </button>
      </div>

      {invoices.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          <div className="p-3 bg-emerald-50 rounded-lg"><p className="text-[10px] text-emerald-600 uppercase">Invoiced Qty</p><p className="text-sm font-bold text-emerald-700">{fmt3(totalQty)}</p></div>
          <div className="p-3 bg-blue-50 rounded-lg"><p className="text-[10px] text-blue-600 uppercase">Subtotal</p><p className="text-sm font-bold text-blue-700">₹{fmt(totalSubtotal)}</p></div>
          <div className="p-3 bg-indigo-50 rounded-lg"><p className="text-[10px] text-indigo-500 uppercase">Total (incl. GST)</p><p className="text-sm font-bold text-indigo-700">₹{fmt(totalInvoiced)}</p></div>
        </div>
      )}

      {loading ? <p className="text-xs text-gray-400">Loading…</p> : invoices.length === 0 ? (
        <div className="text-center py-8 text-gray-400"><FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" /><p className="text-sm">No service invoices yet.</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
          <table className="w-full text-xs min-w-[720px]">
            <thead>
              <tr className="bg-gray-50 text-[10px] text-gray-500 uppercase">
                <th className="text-left py-2 px-3">Invoice #</th>
                <th className="text-left py-2 px-3">Date</th>
                <th className="text-right py-2 px-3">Qty</th>
                <th className="text-right py-2 px-3">Rate</th>
                <th className="text-right py-2 px-3">Subtotal</th>
                <th className="text-right py-2 px-3">GST</th>
                <th className="text-right py-2 px-3">Total</th>
                <th className="text-right py-2 px-3 w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} data-testid={`si-row-${inv.id}`} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2 px-3 font-semibold text-gray-800">{inv.invoice_number}</td>
                  <td className="py-2 px-3 text-gray-600">{inv.date}</td>
                  <td className="py-2 px-3 text-right">{fmt3(inv.total_quantity)} {inv.quantity_unit}</td>
                  <td className="py-2 px-3 text-right">₹{fmt(inv.rate_per_unit)}</td>
                  <td className="py-2 px-3 text-right">₹{fmt(inv.subtotal)}</td>
                  <td className="py-2 px-3 text-right text-gray-500">₹{fmt((inv.cgst || 0) + (inv.sgst || 0) + (inv.igst || 0))}</td>
                  <td className="py-2 px-3 text-right font-bold text-indigo-700">₹{fmt(inv.total)}</td>
                  <td className="py-2 px-3 text-right">
                    <button data-testid={`si-delete-${inv.id}`} onClick={() => handleDelete(inv.id)} className="text-red-500 hover:underline text-[11px]"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialogOpen && <CreateDialog project={project} onClose={() => setDialogOpen(false)} onSaved={() => { setDialogOpen(false); load(); }} />}
    </div>
  );
};

export default ServiceInvoiceTab;
